import {
  VTextField,
  VSelect,
  VSwitch,
  VAutocomplete,
} from 'vuetify/components';

import FileUploader from '@/components/form/FileUploader.vue';
import DynamicJsonField from '@/components/form/DynamicJsonField.vue'
import Posters from '@/components/listing/posters/Posters.vue';

import type { FormField } from '@/types/form';

export const CREATE_FORM: FormField[] = [
  {
    value: 'featured_image',
    label: 'Imagen Principal',
    component: FileUploader,
    module: 'activities',
    col: 6,
    required: false,
  },
  {
    value: 'sku',
    label: 'SKU',
    component: VTextField,
    col: 6,
    required: false,
  },
  {
    value: 'description',
    label: 'Nombre',
    component: VTextField,
    col: 6,
    required: true,
  },
  {
    value: 'organization',
    label: 'Organización',
    component: VAutocomplete,
    fetchOptions: 'searchOrganizations',
    itemText: (item: any) => item.legal_name,
    itemValue: (item: any) => item,
    required: true,
    col: 6,
    transform: (value: any) => ({
      organization_id: value?.id ?? value,
    })
  },
  {
    value: 'product_key',
    label: 'Clave SAT',
    component: VTextField,
    col: 4,
    required: false,
  },
  {
    value: 'taxability',
    label: 'Tipo de Impuesto',
    component: VSelect,
    col: 4,
    fetchOptions: 'searchTaxes',
    itemText: 'text',
    itemValue: 'value',
    required: false,
  },
  {
    value: 'unit_name',
    label: 'Unidad de Medida',
    component: VSelect,
    col: 4,
    fetchOptions: 'searchProductUnits',
    itemText: 'name',
    itemValue: 'name',
    required: false,
  },
  {
    value: 'iva_percentage',
    label: '% IVA',
    component: VTextField,
    col: 4,
    required: false,
    type: 'number',
  },
  {
    value: 'isr_percentage',
    label: '% ISR',
    component: VTextField,
    col: 4,
    required: false,
    type: 'number',
  },
  {
    value: 'ieps_percentage',
    label: '% IEPS',
    component: VTextField,
    col: 4,
    required: false,
    type: 'number',
  },
  {
    value: 'tax_included',
    label: 'Impuesto incluido',
    component: VSwitch,
    col: 12,
    required: false,
    transform: (v: boolean) => ({ tax_included: v ? 1 : 0 }), 
  },
  {
    value: 'prices',
    label: 'Lista de Precios',
    isJsonArray: true,
    component: DynamicJsonField,
    col: 12,
    transform: (val: any[]) => {
      return (val || []).map((type: any) => ({
        amount: type.amount,
        client_type_id: type.client_type_id || (typeof type.client_type === 'object' ? type.client_type.id : type.client_type),
        product_type: "App\\Models\\Product"
      }));
    },
    children: [
      {
        value: 'client_type',
        label: 'Tipo de Cliente',
        searchValue: 'name',
        component: VAutocomplete,
        fetchOptions: 'getClientTypes',
        itemText: 'name',
        itemValue: (item: any) => item,
        required: true,
        col: 6,
        transform: (value: any) => ({
          client_type_id: value?.id,
        })
      },
      {
        value: 'amount',
        label: 'Precio',
        component: VTextField,
        required: true,
        type: 'number',
        col: 6,
        prefix: '$'
      }
    ]
  }
  
];

const idFieldMappings: Record<string, string> = {
  organization_id: 'organization',
};

export const EDIT_FORM: FormField[] = [
  ...CREATE_FORM.map(field => {
    const relatedKey = idFieldMappings[field.value];
    if (relatedKey) {
      return {
        ...field,
        parse: (_: any, item: any) => item[relatedKey]?.id ?? item[relatedKey] ?? null,
        transform: (val: any) => ({
          [field.value]: val?.id ?? val
        })
      };
    }
    if (field.value == 'tax_included'){
      return {
        ...field,
        // cuando hidratas el form desde el item del backend:
        parse: (_: any, item: any) => Boolean(Number(item?.tax_included)),
        transform: (v: boolean) => ({ tax_included: v ? 1 : 0 }),
      }
    }
    return field;
  })
];

export const HEADERS = [
  { title: 'Id', value: 'id', format: 'text', sortable: true },
  { title: 'Nombre', value: 'attributes.description', format: 'text', sortable: false },
  { title: 'SKU', value: 'attributes.sku', format: 'text', sortable: false },
  { title: 'Organización', value: 'attributes.organization.legal_name', format: 'text', sortable: false },
];

export const DYNAMIC_COLUMNS = {
  field: 'attributes.prices',
  key: 'client_type.name',
  label: (name: string) => `Precio ${name}`,
  value: 'amount',
  format: 'currency',
};

export const EXPAND_HEADERS = [
  { title: 'SKU', value: 'attributes.sku', format: 'text' },
  { value: 'actions', sortable: false, align: 'end' },
];

export const EXPAND_DYNAMIC_COLUMNS = {
  field: 'attributes.prices',
  key: 'client_type.name',
  label: (name: string) => `Precio ${name}`,
  value: 'amount',
  format: 'currency',
};

export const DEFAULT_HEADER_ORDER = [
  'id',
  'attributes.description',
  'attributes.sku',
  'attributes.organization.legal_name',
];

export const DETAIL = {
  expand_table: true,
  eye_detail: true,
  component: Posters,
  key: 'attributes.productable',
  custom_expand_detail:true
};

export const FILTERS = [
  {
    value: 'organization',
    label: 'Organización',
    component: VAutocomplete,
    fetchOptions: 'searchOrganizations',
    searchValue: 'name',
    itemText: (item: any) => item?.legal_name || item?.name || '', 
    itemValue: (item: any) => item,
    transform: (value: any) => ({
      organization_id: value?.id ?? value
    })

  },
  {
    value: 'description',
    label: 'Nombre',
    component: VTextField,
  },
  {
    value: 'sku',
    label: 'SKU',
    component: VTextField,
  },
];

export const ROW_ACTIONS = [
  { label: 'Editar', onClick: '__edit', permission: 'products.update' },
  { label: 'Eliminar', onClick: '__delete', permission: 'products.delete' },
];
